import React, { useEffect } from 'react';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppFrame } from './layout/AppFrame';
import { OnboardingFrame } from './layout/OnboardingFrame';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useInviteClaim } from '../hooks/useInviteClaim';
import { isOnboardingLocked, onboardingRedirect, onboardingStageUnknown } from '../lib/onboardingRedirect';
import { Button, PageState } from './v2/ds';

function SettingUp({ text }: { text?: string }) {
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        {text && <p className="text-gray-600 font-medium">{text}</p>}
      </div>
    </div>
  );
}

/** users.me failed, or came back without a stage: say so plainly, never fall through to the portal. */
function AccountError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-screen flex-col bg-page">
      <PageState title="We could not load your account" actions={<Button primary onClick={onRetry}>Try again</Button>}>
        Your portal opens once your account loads. If this keeps happening, contact your GlobalBridge team.
      </PageState>
    </div>
  );
}

export function ProtectedRoute() {
  const { user, loading, error, refetch } = useCurrentUser();
  const { pathname } = useLocation();

  // Claim invite token if present (post-signup brand assignment)
  const { claiming } = useInviteClaim();

  // Set document title based on user's brand
  useEffect(() => {
    if (user?.brand?.displayName) {
      document.title = `${user.brand.displayName} Portal`;
    } else {
      document.title = 'GlobalBridge Portal';
    }
  }, [user?.brand?.displayName]);

  // A brand that is onboarding reaches only /onboarding (spec D9). Wait for
  // users.me first, so the portal never flashes or fires its queries.
  const redirect = onboardingRedirect(pathname, user);
  let body: React.ReactNode;
  if (claiming) body = <SettingUp text="Setting up your account..." />;
  else if (loading) body = <SettingUp />;
  else if ((error && !user) || onboardingStageUnknown(user)) body = <AccountError onRetry={() => void refetch()} />;
  else if (redirect) body = <Navigate to={redirect} replace />;
  else if (isOnboardingLocked(user))
    body = (
      <OnboardingFrame>
        <Outlet />
      </OnboardingFrame>
    );
  else
    body = (
      <AppFrame>
        <Outlet />
      </AppFrame>
    );

  return (
    <>
      <SignedIn>{body}</SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
    </>
  );
}
